import React, { useMemo, useState } from 'react';
import DateTimePicker from 'react-datetime-picker';
import { FormControlLabel, Checkbox, Card, CardContent } from '@material-ui/core';
import { useHistory } from 'react-router';
import { useQuery } from 'react-query';
import { Spinner } from 'react-activity';
import toast from 'react-hot-toast';

import {
  FineDetailDto,
  FineStatus,
  FineStatusDisplay,
  PaymentPartnerFineFilterOptions,
} from '@project/types';
import {
  BasicTable,
  Button,
  InternalSelect,
  PaginationButtons,
  SearchField,
} from '@project/components';
import { finesService, paymentPartnersApiService } from '@project/services';
import { dateTimeFormatterGmtZoneOffset } from '@project/helpers';
import { KeyValuePair } from '@project/types/common.types';
import { useDownloadFile } from '@project/hooks/global';

const availableFilterStatuses = [
  FineStatus.ProvisionallyPaid,
  FineStatus.SettledWithMunicipality,
  FineStatus.PaymentAcknowledgedByServiceProvider,
];

const AdminPaymentPartnersFinesScreen: React.FC = () => {
  const [fineFilterState, setFineFilterState] = useState<PaymentPartnerFineFilterOptions>({
    byNag: false,
    noticeNumber: '',
    pageNum: 1,
    pageEntries: 10,
    invoiceItemCreationReferenceId: null,
    fromDate: null,
    toDate: null,
    status: FineStatus.ProvisionallyPaid,
    paymentPartner: true,
  });
  const history = useHistory();

  const fineStatuses = availableFilterStatuses.map((fineStatus) => {
    return {
      key: fineStatus.toString(),
      value: FineStatusDisplay[fineStatus],
    } as KeyValuePair<string>;
  });

  const { data: paymentPartners, isFetching: isFetchingPaymentPartners } = useQuery(
    ['payment-partners'],
    () => paymentPartnersApiService.get(),
    { refetchOnWindowFocus: false },
  );

  const paymentPartnersKeyPairs = paymentPartners?.map((item) => ({
    value: item.displayName,
    key: item.id,
  }));

  const paymentPartnerId = paymentPartners ? paymentPartners[0].id : null;

  if (paymentPartnerId && fineFilterState.invoiceItemCreationReferenceId === null) {
    setFineFilterState({
      ...fineFilterState,
      invoiceItemCreationReferenceId: paymentPartnerId,
    });
  }

  const { data: paginatedFilteredFines, isFetching: isFetchingPaginatedFilteredFines } = useQuery(
    ['payment-partner-fines', fineFilterState],
    () => finesService.get(fineFilterState),
    {
      enabled: !!paymentPartnerId,
    },
  );

  const onChangePaymentPartner = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setFineFilterState({
      ...fineFilterState,
      invoiceItemCreationReferenceId: event.target.value,
      pageNum: 1,
    });
  };

  const onChangeFineStatus = (event: React.ChangeEvent<HTMLSelectElement>): void => {
    setFineFilterState({
      ...fineFilterState,
      status: Number(event.target.value),
      pageNum: 1,
    });
  };

  const onChangeNoticeNumber = (value: string): void => {
    setFineFilterState({
      ...fineFilterState,
      noticeNumber: value,
      pageNum: 1,
    });
  };

  const onChangeToDate = (value: string): void => {
    setFineFilterState({
      ...fineFilterState,
      toDate: value,
      pageNum: 1,
    });
  };

  const onChangeFromDate = (value: string): void => {
    setFineFilterState({
      ...fineFilterState,
      fromDate: value,
      pageNum: 1,
    });
  };

  const { download, loading } = useDownloadFile({
    apiDefinition: () => finesService.getCsv(fineFilterState),
    onError: () => toast.error('Could not download report'),
  });

  const onClickDownloadCsv = (): void => {
    download();
  };

  const setPage = (pageNumber: number, pageEntries: number): void => {
    setFineFilterState({
      ...fineFilterState,
      pageNum: pageNumber,
      pageEntries,
    });
  };

  const onClickPayments = (): void => {
    history.push('/admin/payment-partners/payments');
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Notice No',
        accessor: 'noticeNumber',
      },
      {
        Header: 'Reg No',
        accessor: 'vehicleRegistration',
      },
      {
        Header: 'Area',
        accessor: 'area',
      },
      {
        Header: 'Paid Date',
        accessor: (fine: FineDetailDto) =>
          dateTimeFormatterGmtZoneOffset(fine.paidDate.toString(), '/', 2),
      },
      {
        Header: 'Fine Amount',
        accessor: 'fineAmount',
      },
      {
        Header: 'Amount Paid',
        accessor: 'amountPaid',
      },
      {
        Header: 'Discount',
        accessor: 'discount',
      },
      {
        Header: 'Other Fees',
        accessor: 'otherFees',
      },
      {
        Header: 'Total',
        accessor: 'total',
      },
      {
        Header: 'Fine Owner',
        accessor: (fine: FineDetailDto) => (fine.fineOwned ? 'Yes' : 'No'),
      },
    ],
    [],
  );

  return (
    <>
      <div className="flex flex-row justify-between">
        <div className="mt-2 flex justify-start mx-8 text-xl">Payment Partner Fines</div>
        <div className="flex mr-8 space-x-2 mt-2">
          <Button
            className="bg-tertiary text-white hover:bg-tertiary-dark"
            size="small"
            isLoading={loading}
            onClick={onClickDownloadCsv}
          >
            Download CSV
          </Button>
          <Button
            className="bg-tertiary text-white hover:bg-tertiary-dark"
            size="small"
            onClick={onClickPayments}
          >
            Process Payments
          </Button>
        </div>
      </div>
      <div className="border-b border-black mt-2 flex justify-start mx-8" />
      <div className="flex flex-col pt-2">
        <div className="flex  w-full px-8 gap-4">
          <div className="flex flex-col w-full">
            <label className="text-sm font-semibold mb-1">Notice Number</label>
            <SearchField
              placeholder="Notice Number"
              value={fineFilterState.noticeNumber}
              onChange={onChangeNoticeNumber}
              isLoading={isFetchingPaginatedFilteredFines}
              dependencies={fineFilterState}
            />
          </div>
          {isFetchingPaymentPartners ? null : (
            <div className="flex flex-col w-full">
              <label className="text-sm font-semibold mb-1">Payment Partner</label>
              <InternalSelect
                className="outline-none rounded-md h-11 shadow-mui"
                options={paymentPartnersKeyPairs}
                onChange={onChangePaymentPartner}
              />
            </div>
          )}
          <div className="flex flex-col w-full">
            <label className="text-sm font-semibold mb-1">Status</label>
            <InternalSelect
              className="outline-none rounded-md h-11 shadow-mui"
              options={fineStatuses}
              onChange={onChangeFineStatus}
            />
          </div>
        </div>
        <div className="flex w-full px-8 gap-4">
          <div className="flex mt-4">
            <FormControlLabel
              control={
                <Checkbox
                  value={fineFilterState.byNag}
                  onChange={() =>
                    setFineFilterState({
                      ...fineFilterState,
                      byNag: !fineFilterState.byNag,
                      pageNum: 1,
                    })
                  }
                />
              }
              label="Only NAGs"
            />
          </div>
          <div className="flex flex-col w-40">
            <label htmlFor="filter-date-from" className="text-sm font-semibold mb-2">
              Filter From
            </label>
            <DateTimePicker
              id="filter-date-from"
              clearIcon={null}
              format="y-MM-dd"
              onChange={onChangeFromDate}
              value={fineFilterState.fromDate}
              disableClock
            />
          </div>
          <div className="flex flex-col w-40">
            <label htmlFor="filter-date-to" className="text-sm font-semibold mb-2">
              Filter To
            </label>
            <DateTimePicker
              id="filter-date-to"
              clearIcon={null}
              format="y-MM-dd"
              onChange={onChangeToDate}
              value={fineFilterState.toDate}
              disableClock
            />
          </div>
        </div>
      </div>
      <>
        <div className="flex flex-col flex-grow mx-8 mt-2">
          <Card className="flex flex-grow shadow-2xl">
            <CardContent className="flex flex-col flex-grow p">
              {!isFetchingPaginatedFilteredFines && !isFetchingPaymentPartners ? (
                <BasicTable
                  tableData={paginatedFilteredFines.items}
                  tableColumns={columns}
                  path="./payment-partners"
                  isViewable={false}
                />
              ) : (
                <div className="flex items-center justify-center flex-grow">
                  <Spinner />
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {!isFetchingPaginatedFilteredFines && !isFetchingPaymentPartners ? (
          <PaginationButtons
            page={fineFilterState.pageNum}
            setPage={setPage}
            pageIndex={paginatedFilteredFines.pageIndex}
            totalPages={paginatedFilteredFines.totalPages}
            pageEntries={fineFilterState.pageEntries}
          />
        ) : (
          <div className="h-13" />
        )}
      </>
    </>
  );
};
export default AdminPaymentPartnersFinesScreen;
